import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * FILE: AuthContext.js
 * PURPOSE: Single source of truth for "who is the current Ghost identity" across the app.
 *
 * There are no real accounts/passwords here — `token` is a bearer session token issued by
 * the backend Vault engine after OTP/social verification (see utils/api.js verifyOTP /
 * socialHandshake / finalizeGhost), and `anonymousId` is the pseudonymous identity string
 * tied to that token. `isVerified` is simply "do we currently hold a token" — it does not
 * mean the token is still valid server-side (see App.js's IDENTITY_INVALID handling, which
 * force-logs-out when the backend rejects a stale token).
 *
 * Persistence model: token/anonymousId/email each get their own localStorage key, mirrored
 * via three independent useEffects below. This is plain, unencrypted localStorage (not an
 * httpOnly cookie), so it is readable by any JS running on the page — see audit notes for
 * the XSS/token-theft implication.
 */
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    // Initial state is read synchronously from localStorage so a page refresh doesn't
    // flash a "logged out" state before the first render.
    const [token, setToken] = useState(localStorage.getItem('ghost_token') || null);
    const [anonymousId, setAnonymousId] = useState((localStorage.getItem('ghost_id') || '').trim() || null);
    const [email, setEmail] = useState(localStorage.getItem('ghost_email') || null);
    const [isVerified, setIsVerified] = useState(!!localStorage.getItem('ghost_token'));

    // Keep localStorage['ghost_token'] and isVerified in sync with `token` state.
    // Note: EnrollmentModal.js writes 'ghost_token'/'ghost_id' to localStorage directly in a
    // few places (bypassing this context) as an optimization so utils/api.js can read the
    // fresh token immediately, then calls login() afterwards to update this context's state.
    useEffect(() => {
        if (token) {
            localStorage.setItem('ghost_token', token);
            setIsVerified(true);
        } else {
            localStorage.removeItem('ghost_token');
            setIsVerified(false);
        }
    }, [token]);

    useEffect(() => {
        if (anonymousId) {
            localStorage.setItem('ghost_id', anonymousId);
        } else {
            localStorage.removeItem('ghost_id');
        }
    }, [anonymousId]);

    useEffect(() => {
        if (email) {
            localStorage.setItem('ghost_email', email);
        } else {
            localStorage.removeItem('ghost_email');
        }
    }, [email]);

    const login = (newToken, newId, newEmail) => {
        setToken(newToken);
        setAnonymousId(newId ? newId.trim() : null);
        setEmail(newEmail);
    };

    const logout = () => {
        setToken(null);
        setAnonymousId(null);
        setEmail(null);
    };

    return (
        <AuthContext.Provider value={{ token, anonymousId, email, isVerified, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
